import { Navigate } from 'react-router-dom';
import { useInstructorSettlementVisible, useSeminarPaymentEnabled } from '@/common/hooks';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import PageState from '@/components/common/PageState';
import { AccountPageHeader, AccountPageShell } from '@/components/common/organism/AccountPageLayout';

interface SeminarPaymentFeatureRouteProps {
    children: React.ReactNode;
    /**
     * 무엇으로 판단할지 — `enabled`(기본): 커리어가 공개돼 있어야 함(쿠폰·강사 정산),
     * `history`: 결제내역을 볼 수 있으면 됨(공개 중이거나 결제 기록이 있는 회원 — 커리어를 꺼도 구매자는 그대로),
     * `settlement`: 강사 정산(공개 중이거나 강사 본인에게 판매 기록이 있음 — 강사가 아니면 정산 조회가 실패해 막힌다)
     */
    gate?: 'enabled' | 'history' | 'settlement';
}

/**
 * 커리어(세미나 결제) 공개 상태로 화면 접근을 막는다 — 메뉴에서 숨겨도 URL 직접 접근을 막는 용도.
 * 막히면 홈으로 돌려보낸다. 강사 정산은 대상이 아니라는 안내를 보여 준다(말없이 홈으로 가면 고장처럼 보인다, 2026-10-11).
 */
const SeminarPaymentFeatureRoute = ({ children, gate = 'enabled' }: SeminarPaymentFeatureRouteProps) => {
    const { seminarPaymentEnabled, seminarPaymentHistoryVisible, seminarPaymentFlagLoaded } = useSeminarPaymentEnabled();
    const { settlementVisible, settlementLoaded } = useInstructorSettlementVisible(gate === 'settlement');

    if (!seminarPaymentFlagLoaded || (gate === 'settlement' && !settlementLoaded)) {
        return <LoadingSpinner placement="page" minHeight="40vh" />;
    }

    const allowed: boolean = gate === 'history' ? seminarPaymentHistoryVisible : gate === 'settlement' ? settlementVisible : seminarPaymentEnabled;
    if (!allowed && gate === 'settlement') {
        return (
            <AccountPageShell data-account-page="">
                <AccountPageHeader title="정산" />
                <PageState
                    title="아직 정산할 판매 내역이 없어요"
                    description="명치사 커리어 세미나를 판매한 강사에게 정산 내역이 생겨요."
                    action={{ label: '세미나 이력 보기', to: '/seminars/history' }}
                />
            </AccountPageShell>
        );
    }
    if (!allowed) {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
};

export default SeminarPaymentFeatureRoute;
